<script setup>
import { ref, onMounted, computed } from 'vue'
import DataTable from 'datatables.net-vue3'
import DataTablesCore from 'datatables.net-dt'

const props = defineProps({
  jobs: Array,
});

const options = {
  lengthChange: false,
  order: [[7, 'desc']],
  language: {
    emptyTable: "No jobs available"
  }
}

const getStatusBadge = (status) => {
  switch (status) {
    case 1: return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Draft</span>'
    case 2: return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>'
    case 3: return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">Expired</span>'
    case 4: return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Closed</span>'
    default: return '<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>'
  }
}

const getPaymentMethod = (method, status) => {
  if (status === 1) return '-' // Draft jobs don't have payment yet
  
  switch (method) {
    case 'credit': return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Credit</span>'
    case 'payment': return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Paid</span>'
    case 'free': return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Free</span>'
    default: return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>'
  }
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const getExpiryDate = (publishDate) => {
  if (!publishDate) return null
  const expiry = new Date(publishDate)
  expiry.setDate(expiry.getDate() + 30)
  return expiry
}

const getRemainingDays = (publishDate) => {
  if (!publishDate) return 0
  
  const expiry = getExpiryDate(publishDate)
  const now = new Date()
  const diff = expiry - now
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

const columns = [
  { 
    data: 'title', 
    title: 'Job Details', 
    width: '25%',
    render: function(data, type, row) {
      let html = `<div class="text-sm font-medium text-gray-900">${row.title}</div>`
      html += `<div class="text-xs text-gray-500 mt-1">${row.companyName}</div>`
      return html
    }
  },
  { 
    data: null, 
    title: 'Industry & Role',
    width: '15%',
    render: function(data, type, row) {
      return `<div class="text-sm text-gray-900">${row.industryName || '-'}</div>
              <div class="text-xs text-gray-500 mt-1">${row.roleName || '-'}</div>`
    }
  },
  { 
    data: 'work_location', 
    title: 'Location',
    width: '12%',
    render: function(data, type, row) {
      const location = row.work_location || '-'
      const workArrangement = row.work_arrangement ? 
        (() => {
          switch(row.work_arrangement) {
            case 'wfh': return 'WFH'
            case 'field': return 'Field'
            case 'hybrid': return 'Hybrid'
            case 'office': return 'Office / On Site'
            case 'other': return 'Other'
            default: return row.work_arrangement
          }
        })() : '-'
      
      return `<div class="text-sm text-gray-900">${location}</div>
              <div class="text-xs text-gray-500 mt-1">${workArrangement}</div>`
    }
  },
  {
    data: null,
    title: 'Dates',
    width: '15%',
    render: function(data, type, row) {
      let dateInfo = ''
      
      if (row.status === 1) {
        dateInfo = `<div class="text-sm text-gray-900">Created: ${formatDate(row.creation_date)}</div>`
      } else if (row.status === 2) {
        const daysLeft = getRemainingDays(row.published_date)
        const remainingText = daysLeft <= 0 ? 'Expires today' : 
                            daysLeft === 1 ? '1 day left' : 
                            `${daysLeft} days left`
        
        const textColor = daysLeft <= 7 ? 'text-orange-600' : 'text-sky-600'
        
        dateInfo = `<div class="text-sm text-gray-900">Published: ${formatDate(row.published_date)}</div>
                    <div class="text-xs ${textColor} mt-1">${remainingText}</div>`
      } else if (row.status === 3) {
        dateInfo = `<div class="text-sm text-gray-900">Expired: ${formatDate(row.expired_date || getExpiryDate(row.published_date))}</div>`
      } else if (row.status === 4) {
        dateInfo = `<div class="text-sm text-gray-900">Closed: ${formatDate(row.closed_date)}</div>`
      }
      
      return dateInfo
    }
  },
  {
    data: 'totalApplications',
    title: 'Applicants',
    width: '8%',
    render: function(data, type, row) {
      return `<div class="text-sm font-medium ${Number(data) > 0 ? 'text-sky-600' : 'text-gray-900'}">${data}</div>`
    }
  },
  {
    title: 'Status',
    width: '10%',
    render: function(data, type, row) {
      return getStatusBadge(row.status || (row.is_active == 1 ? 2 : 4))
    }
  },
  {
    title: 'Payment',
    width: '10%',
    render: function(data, type, row) {
      return getPaymentMethod(row.payment_method || 'unknown', row.status)
    }
  },
  {
    data: null,
    title: 'Actions',
    width: '10%',
    render: function(data, type, row) {
      let actions = `<div class="flex space-x-2">`
      
      // View action
      actions += `<a href="/dashboard/job/${row.uuid}" class="p-1.5 rounded-md hover:bg-sky-100 text-sky-600" title="View Details">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  </a>`
      
      
      actions += `</div>`
      return actions
    },
    className: 'text-center',
    searchable: false,
    orderable: false
  }
]

DataTable.use(DataTablesCore)

let dt
const table = ref()
 
onMounted(function () {
  dt = table.value.dt
})
</script>

<style>
.dataTables_wrapper .dataTables_filter {
  @apply pb-4 text-sky-700;
}

.dataTables_wrapper .dataTables_filter input {
  @apply border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500 ml-2;
}

.dataTables_wrapper .dataTables_info {
  @apply text-sm text-gray-600 pt-4;
}

.dataTables_wrapper .dataTables_paginate {
  @apply pt-4;
}

.dataTables_wrapper .dataTables_paginate .paginate_button {
  @apply border border-gray-300 rounded px-3 py-1 mx-1 text-sky-700 hover:bg-sky-50;
}

.dataTables_wrapper .dataTables_paginate .paginate_button.current {
  @apply bg-sky-100 text-sky-700 font-medium;
}

.dataTables_wrapper .dataTables_paginate .paginate_button.disabled {
  @apply text-gray-400;
}
</style>

<template>
  <div class="overflow-hidden">
    <DataTable ref="table" class="cell-border w-full" :columns="columns" :data="jobs" :options="options"></DataTable>
  </div>
</template>